import { UmmeldungResponse } from '../../../generated/model/ummeldung-response.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { UmmeldungService } from '../../service/ummeldung/ummeldung.service';
import { computed, inject } from '@angular/core';
import { UmmeldungRequest } from '../../../generated/model/ummeldung-request.model';
import { catchError, forkJoin, from, map, Observable, of, pipe, switchMap, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Ummeldung } from '../../../generated/model/ummeldung.model';
import { withSessionStorage, withStorageSync } from '@angular-architects/ngrx-toolkit';
import { UmmeldungMetadata } from '../../../generated/model/ummeldung-metadata.model';
import { withTreeShakableDevTools } from '../../store/features/devTools.feature';
import { withUmmeldungFeatures } from '../meter-point/store/ummeldung.feature';
import { MeterPoint } from '../../model/meter-point.model';
import { Company } from '@shared-ikp/model/company.model';
import { CompanyService } from '../../service/company/company.service';
import { buildBisherigerKunde } from './utils';
import { withRequestTracking } from '../../store/features/request-tracking.feature';
import { MeterpointStatus } from '../../model/meterpoint-status.model';
import { VertragspartnerMode } from './schemas';

interface UmmeldungState {
  activeUmmeldung: Ummeldung | null;
  aktuellerZaehlpunkt: MeterPoint | null;
  zaehlpunktUnternehmen: Company | null;
  accountUnternehmen: Company[];
  verwalteteUnternehmen: Company[];
  metadata: UmmeldungMetadata;
  loading: boolean;
  dataReady: boolean;
  error: HttpErrorResponse | null;
}

const initialState: UmmeldungState = {
  activeUmmeldung: null,
  aktuellerZaehlpunkt: null,
  zaehlpunktUnternehmen: null,
  accountUnternehmen: [],
  verwalteteUnternehmen: [],
  metadata: {
    letzterBearbeiter: {
      vorname: null,
      nachname: null,
      email: null,
    },
    letztesBearbeitungsDatum: null,
    erstellungsDatum: null,
  },
  loading: false,
  dataReady: false,
  error: null,
};

export const UmmeldungStore = signalStore(
  withTreeShakableDevTools('UmmeldungStore'),
  withState<UmmeldungState>(initialState),
  withUmmeldungFeatures(),
  withStorageSync(
    {
      key: 'ummeldung',
      select: (state: UmmeldungState) => ({
        activeUmmeldung: state.activeUmmeldung,
        aktuellerZaehlpunkt: state.aktuellerZaehlpunkt,
        zaehlpunktUnternehmen: state.zaehlpunktUnternehmen,
      }),
    },
    withSessionStorage(),
  ),
  withRequestTracking(),
  withComputed(store => ({
    isZaehlpunktAktiv: computed(() => store.aktuellerZaehlpunkt()?.status === MeterpointStatus.AKTIV),
    bisherigerVertragspartnerMode: computed(() => {
      if (store.aktuellerZaehlpunkt()?.status !== MeterpointStatus.AKTIV) {
        return VertragspartnerMode.BEARBEITBAR;
      }

      return store.aktuellerZaehlpunkt()?.hasAbweichenderRechnungsempfaenger
        ? VertragspartnerMode.VERWALTETER_KUNDE
        : VertragspartnerMode.ACCOUNT_KUNDE;
    }),
  })),
  withMethods((store, service = inject(UmmeldungService), companyService = inject(CompanyService)) => {
    return {
      getUmmeldungById(id: string): Observable<Ummeldung> {
        patchState(store, { loading: true });
        return service.getUmmeldung({ id }).pipe(
          tap((response: UmmeldungResponse) => {
            patchState(store, {
              activeUmmeldung: { id, ...response.ummeldung },
              metadata: response.ummeldungMetadata,
              loading: false,
              error: null,
            });
            this.loadZaehlpunktUnternehmen();
          }),
          map(response => ({ id, ...response.ummeldung })),
          catchError((error: HttpErrorResponse) => {
            patchState(store, { activeUmmeldung: null, loading: false, error });
            return throwError(() => error);
          }),
        );
      },
      setAktuellerZaehlpunkt(aktuellerZaehlpunkt: MeterPoint): void {
        patchState(store, { aktuellerZaehlpunkt });
      },
      setDataReady(dataReady: boolean): void {
        patchState(store, { dataReady });
      },
      loadZaehlpunktUnternehmen: rxMethod<void>(
        pipe(
          map(() => store.aktuellerZaehlpunkt()),
          switchMap(zaehlpunkt => {
            patchState(store, { zaehlpunktUnternehmen: null, accountUnternehmen: [], verwalteteUnternehmen: [] });

            if (!zaehlpunkt) {
              return of(null);
            }

            return forkJoin({
              zaehlpunktUnternehmen: zaehlpunkt.geschaeftspartnerNr
                ? from(companyService.loadCompanyByGpNr(zaehlpunkt.geschaeftspartnerNr))
                : of(null),
              accountUnternehmen: from(companyService.loadAllCompanies()),
              verwalteteUnternehmen: companyService.loadManagedCompanies(),
            }).pipe(
              tap(({ zaehlpunktUnternehmen, accountUnternehmen, verwalteteUnternehmen }) => {
                patchState(store, { zaehlpunktUnternehmen: zaehlpunktUnternehmen ?? null, accountUnternehmen, verwalteteUnternehmen });

                const mode = store.bisherigerVertragspartnerMode();
                const activeUmmeldung = store.activeUmmeldung();
                if (mode === VertragspartnerMode.BEARBEITBAR || !zaehlpunktUnternehmen || !activeUmmeldung) {
                  return;
                }

                patchState(store, {
                  activeUmmeldung: {
                    ...activeUmmeldung,
                    bisherigerKunde: buildBisherigerKunde({
                      mode,
                      zaehlpunkt,
                      vertragspartnerUnternehmen: zaehlpunktUnternehmen,
                      bisherigerKunde: activeUmmeldung.bisherigerKunde,
                    }),
                  },
                });
              }),
              catchError(() => {
                patchState(store, { zaehlpunktUnternehmen: null, accountUnternehmen: [], verwalteteUnternehmen: [] });
                return of(null);
              }),
            );
          }),
        ),
      ),
      createUmmeldung(ummeldungRequest: UmmeldungRequest): Observable<void> {
        return store.trackRequest(service.createUmmeldung({ ummeldungRequest })).pipe(
          tap((id: string) => patchState(store, { activeUmmeldung: { id, ...ummeldungRequest.ummeldung } })),
          map(() => void 0),
        );
      },
      updateUmmeldung(id: string, ummeldungRequest: UmmeldungRequest): Observable<void> {
        return store.trackRequest(service.updateUmmeldung({ id, ummeldungRequest })).pipe(map(() => void 0));
      },
      deleteUmmeldung(id: string): Observable<void> {
        return store.trackRequest(service.deleteUmmeldung({ id })).pipe(map(() => void 0));
      },
      submitUmmeldung(id: string): Observable<void> {
        return store.trackRequest(service.submitUmmeldung({ id })).pipe(map(() => void 0));
      },
      uploadUmmeldungPdf(id: string, file: File): Observable<void> {
        return store.trackRequest(service.uploadUmmeldungPdf({ id, body: file })).pipe(map(() => void 0));
      },
      getUmmeldungPdf(id: string): Observable<Blob> {
        return service.getUmmeldungPdf({ id });
      },
      saveActiveUmmeldung(aktuellerProzessSchritt: Ummeldung.AktuellerProzessSchrittEnum | null): Observable<void> {
        const activeUmmeldung = store.activeUmmeldung();
        if (!activeUmmeldung) {
          return of(void 0);
        }
        const ummeldung: Ummeldung = { ...activeUmmeldung, status: Ummeldung.StatusEnum.BEGONNEN, aktuellerProzessSchritt };
        if (activeUmmeldung.id) {
          return this.updateUmmeldung(activeUmmeldung.id, { ummeldung });
        }
        return this.createUmmeldung({ ummeldung });
      },
      setActiveUmmeldung(ummeldung: Partial<Ummeldung>): void {
        patchState(store, { activeUmmeldung: { ...ummeldung } as Ummeldung });
      },
      updateActiveUmmeldung(partial: Partial<Ummeldung>) {
        const activeUmmeldung = store.activeUmmeldung();
        if (activeUmmeldung) {
          patchState(store, { activeUmmeldung: { ...activeUmmeldung, ...partial } });
        }
      },
      clearStore(): void {
        patchState(store, initialState);
        sessionStorage.removeItem('ummeldung');
      },
    };
  }),
  withComputed(state => ({
    isActiveZaehlpunkt: computed(() => state.aktuellerZaehlpunkt.status() === MeterpointStatus.AKTIV),
  })),
);
